import { useMemo } from "react";
import { Button } from "../ui";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import { navigateExternal } from "../lib/postmessage";
import { ctaTranslate } from "../lib/ctaTranslate";
import { useLabel } from "../lib/labels";
import type { PromotionItem, PromotionList } from "../types";

interface Props {
  data: PromotionList;
  sessionToken: string;
}

/* ─── guide U3 / 3st 제품프로모션 ─── */

const _LG_IMG_CDN = "https://www.lg.com/content/dam/channel/wcms";
const _SWIPER_THRESHOLD = 4;
const _CARDS_PER_PAGE = 3;

// i18n: "Ends/From" 라벨은 훅이라 호출부(컴포넌트)에서 받아 주입한다(useLabel 폴백 동일).
function formatDateRange(
  startsAt: string | undefined,
  endsAt: string | undefined,
  labels: { ends: string; from: string },
): string | null {
  const fmt = (v?: string) => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };
  const s = fmt(startsAt);
  const e = fmt(endsAt);
  if (s && e) return `${s} ~ ${e}`;
  if (e) return labels.ends.replace("{date}", e);
  if (s) return labels.from.replace("{date}", s);
  return null;
}

function resolveImageUrl(path?: string): string | null {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${_LG_IMG_CDN}${path.startsWith("/") ? "" : "/"}${path}`;
}

function isPromotionDetailMode(data: PromotionList): boolean {
  const meta = data.metadata;
  if (!meta) return false;
  return meta.detailMode === true || meta.method === "specific_detail";
}

function isHighlighted(
  item: PromotionItem,
  highlightRootCodes?: string[],
  detailMode = false,
  highlightPromotionIds?: string[],
): boolean {
  if (highlightPromotionIds !== undefined) return highlightPromotionIds.includes(item.id);
  if (detailMode) return true;
  if (!highlightRootCodes?.length) return false;
  return item.rootCodes?.some((rc) => highlightRootCodes.includes(rc)) ?? false;
}

function fallbackPromotionImage(): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="350" height="350" viewBox="0 0 350 350" role="img" aria-label="LG promotion placeholder">
      <rect width="350" height="350" fill="#c8c8c8"/>
      <text x="175" y="175" text-anchor="middle" alignment-baseline="middle"
            fill="#333" font-size="20" font-family="sans-serif">Image</text>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    pages.push(arr.slice(i, i + size));
  }
  return pages;
}

export default function PromotionListCard({ data, sessionToken }: Props) {
  const detailMode = isPromotionDetailMode(data);

  const promotions = useMemo<PromotionItem[]>(() => {
    if (
      data.groupedPromotions &&
      Object.keys(data.groupedPromotions).length > 0
    ) {
      // 여러 모델에 같은 프로모션이 걸리면 그룹마다 반복되므로 id 기준 첫 등장만 남긴다.
      const seen = new Set<string>();
      return Object.values(data.groupedPromotions)
        .flat()
        .filter((p) => !seen.has(p.id) && !!seen.add(p.id));
    }
    return data.promotions ?? [];
  }, [data.groupedPromotions, data.promotions]);

  const sortedPromotions = useMemo(() => {
    const hl = data.highlightRootCodes;
    const ids = data.highlightPromotionIds;
    if (ids === undefined && !hl?.length) return promotions;
    return [...promotions].sort(
      (a, b) =>
        (isHighlighted(b, hl, false, ids) ? 1 : 0) -
        (isHighlighted(a, hl, false, ids) ? 1 : 0),
    );
  }, [promotions, data.highlightRootCodes, data.highlightPromotionIds]);

  const useSwiper = sortedPromotions.length >= _SWIPER_THRESHOLD;
  const pages = useMemo(
    () => (useSwiper ? chunk(sortedPromotions, _CARDS_PER_PAGE) : []),
    [sortedPromotions, useSwiper],
  );

  const noActiveLabel = useLabel(
    "noActive",
    "No active promotions right now.",
  );
  // 08_프로모션 §6: Go to Buying Page는 호스트 linkout 칩으로 일원화되어 카드 내부에는 렌더하지 않음.
  const sectionTitle = data.sectionTitle || "";

  if (sortedPromotions.length === 0) {
    return (
      <div className="ai-gp1--section">
        <div className="ai-gp1-dp--list">
          <p style={{ padding: "16px", color: "#646464" }}>
            {noActiveLabel}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-gp1--section ai-gp1--promo">
      {sectionTitle ? <h4 className="gp1-titel">{sectionTitle}</h4> : null}
      {sortedPromotions.length === 0 ? null : useSwiper ? (
        <Swiper
          className="ai-gp1-dp--list swiper"
          wrapperClass="dp-list swiper-wrapper"
          modules={[Pagination]}
          pagination={{ clickable: true }}
          spaceBetween={12}
          slidesPerView={1}
          watchOverflow={true}
          updateOnWindowResize={true}
        >
          {pages.map((page, pageIdx) => (
            <SwiperSlide key={pageIdx}>
              {page.map((p) => (
                <PromotionItemCard
                  key={p.id}
                  item={p}
                  isHighlighted={isHighlighted(
                    p,
                    data.highlightRootCodes,
                    detailMode,
                    data.highlightPromotionIds,
                  )}
                  sessionToken={sessionToken}
                />
              ))}
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className="ai-gp1-dp--list">
          <div className="dp-list">
            {sortedPromotions.map((p) => (
              <PromotionItemCard
                key={p.id}
                item={p}
                isHighlighted={isHighlighted(
                  p,
                  data.highlightRootCodes,
                  detailMode,
                  data.highlightPromotionIds,
                )}
                sessionToken={sessionToken}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PromotionItemCard({
  item,
  isHighlighted,
  sessionToken,
}: {
  item: PromotionItem;
  isHighlighted: boolean;
  sessionToken: string;
}) {
  const imgUrl = resolveImageUrl(item.image);
  const primaryUrl = item.linkUrl ?? null;
  const secondaryUrl = item.optionalCtaUrl ?? null;
  const dateEndsLabel = useLabel("dateEnds", "Ends {date}");
  const dateFromLabel = useLabel("dateFrom", "From {date}");
  const dateRange = formatDateRange(item.startsAt, item.endsAt, {
    ends: dateEndsLabel,
    from: dateFromLabel,
  });
  const hasDualCta = !!secondaryUrl;

  const buyNowLabel = useLabel("buyNow", "Buy Now");
  const redeemNowLabel = useLabel("redeemNow", "Redeem now");
  const primaryLabel = item.ctaLabel || buyNowLabel;
  const secondaryLabel = item.optionalCtaLabel || redeemNowLabel;

  const doNavigate = (url: string) => {
    navigateExternal(url, undefined, { sessionId: sessionToken });
  };

  const handlePrimaryCta = () => {
    if (primaryUrl) {
      doNavigate(primaryUrl);
      return;
    }
    void ctaTranslate({
      code: "promo_list.show_me_products_for",
      params: { title: item.title },
      sessionToken,
      fallback: `Show me products for "${item.title}"`,
    });
  };

  const handleSecondaryCta = () => {
    if (secondaryUrl) {
      doNavigate(secondaryUrl);
      return;
    }
    handlePrimaryCta();
  };

  const itemClass = isHighlighted ? "dp-item highlight" : "dp-item";

  return (
    <div className={itemClass}>
      <div className="dp-item--inner">
        {dateRange && <div className="dp-date">{dateRange}</div>}
        <div className="dp-info">
          <div className="info-inner">
            <div className="info-title">{item.title}</div>
            {/* AISASSIT-35: 설명이 없어도 .info-desc 를 항상 렌더해 2줄(min-height 28px)
             * 영역을 고정한다(미렌더 시 카드 높이가 들쭉날쭉해지는 문제). */}
            <div className="info-desc">{item.subtitle}</div>
          </div>
          <div className="info-img">
            <img
              src={imgUrl ?? fallbackPromotionImage()}
              alt={item.title}
              loading="lazy"
              draggable={false}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = fallbackPromotionImage();
              }}
            />
          </div>
        </div>
        <div className="dp-btns">
          {hasDualCta ? (
            <>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => handlePrimaryCta()}
              >
                {primaryLabel}
              </Button>
              <Button
                variant="primary"
                size="xs"
                onClick={() => handleSecondaryCta()}
              >
                {secondaryLabel}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="xs"
              onClick={() => handlePrimaryCta()}
            >
              {primaryLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
